import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import DateReservation from 'services/DateReservation'
import ExceptionalDate from 'services/ExceptionalDate'
import Select from 'components/Inputs/Select'
import DateInput from 'components/Inputs/DateInput'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import exceptionalDateValidator from 'validators/exceptionalDateValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  Row,
  InfoText,
  WhiteText,
  WarningText,
  ErrorContainer
} from './styles'

export default function CreateExceptionalDateModal ({
  visible,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [businessHours, setBusinessHours] = useState (null)
  const [professionals, setProfessionals] = useState ([])
  const [startTimeWindows, setStartTimeWindows] = useState ([])
  const [endTimeWindows, setEndTimeWindows] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const [loading, setLoading] = useState (true)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      const [{ data: workingDays }, { data: professionals }] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}/working_days`),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?status=active&deleted=false&access_level=service`,
          requestConfig
        )
      ])

      const businessHours = TimeWindow.getBusinessHours(workingDays)

      setStartTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false)
      )
      
      setEndTimeWindows (
        TimeWindow.getTimeWindows (businessHours?.opening, businessHours?.closing, false)
      )

      setBusinessHours (businessHours)
      setProfessionals ([
        {
          label: 'Todos',
          value: 'all_professionals'
        },
        ...professionals.map (professional => ({
          label: professional.nickname,
          value: professional
        }))
      ])
    } catch (error) {
      setErrorMessage (getApiErrorMessage (error))
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Criar data excepcional para a Cutilei</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={exceptionalDateValidator}
            initialValues={{
              'professionals': [],
              'description': '',
              'startDate': '',
              'endDate': '',
              'period': null,
              'startTime': null,
              'endTime': null
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setErrorMessage (null)

                const exceptionalDateData = {
                  ...values,
                  professionals: ExceptionalDate.getProfessionalIds (professionals, values.professionals),
                  startTime: values.startTime.value,
                  endTime: values.endTime.value
                }

                const { data: exceptionalDate } = await cutileiApi.post (
                  `/businesses/${businessId}/exceptional_dates`, exceptionalDateData, requestConfig
                )

                setSubmitting (false)
                handleConfirm (exceptionalDate)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                setErrorMessage (getApiErrorMessage (error))
                logApiError (error)
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit
            }) => {
              const handleTimeSelected = (name, option) => {
                if (name === 'startTime') {
                  setEndTimeWindows (TimeWindow.getTimeWindows (option.value, businessHours?.closing, true))
                } else if (name === 'endTime') {
                  setStartTimeWindows (TimeWindow.getTimeWindows (businessHours?.opening, option.value, true))
                }
                setFieldValue (name, option)
              }

              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                      <InfoText>
                        A data excepcional <b>abre</b> sua agenda para atendimentos Cutilei na data escolhida, no horário e
                        com os profissionais escolhidos, sem mexer no horário normal do salão.
                        Para <b>fechar</b> sua agenda para a Cutilei em uma data, use as <b>Reservas Cutilei</b>.
                      </InfoText>
                      <Row>
                        <FormField>
                          <Select
                            id='professionals'
                            aria-label='Profissionais'
                            name='professionals'
                            placeholder='Profissionais...'
                            value={values.professionals}
                            options={professionals}
                            isMulti={true}
                            isClearable={true}
                            onRemoveOption={professional => {
                              setFieldValue (
                                'professionals', values.professionals.filter (p => p.value !== professional.value)
                              )
                            }}
                            onChange={(name, selected) => {
                              ExceptionalDate.handleProfessionalsChange (name, selected, setFieldValue)
                            }}
                            onBlur={setFieldTouched}
                            error={errors.professionals}
                            touched={touched.professionals}
                          />
                          {touched.professionals && errors.professionals && (
                            <WarningText>
                              {errors.professionals}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Input 
                            placeholder='Descrição da data'
                            value={values.description}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <DateInput
                            placeholder='Data de início'
                            value={values.startDate}
                            onChange={date => {
                              DateReservation.handleStartDateChange (date, values, setFieldValue)
                            }}
                            style={{width: '100%'}}
                          />
                          {touched.startDate && errors.startDate && (
                            <WarningText>
                              {errors.startDate}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Select
                            name='period'
                            placeholder='Período...'
                            value={values.period}
                            options={DateReservation.PERIODS}
                            onChange={(name, option) => {
                              DateReservation.handlePeriodChange (name, option, values, setFieldValue)
                            }}
                            onBlur={setFieldTouched}
                            error={errors.period}
                            touched={touched.period}
                          />
                          {touched.period && errors.period && (
                            <WarningText>
                              {errors.period}
                            </WarningText>
                          )}
                        </FormField>
                        {values.period?.value === -2 && (
                          <FormField>
                            <DateInput
                              placeholder='Data final'
                              value={values.endDate}
                              onChange={date => setFieldValue ('endDate', date)}
                              style={{width: '100%'}}
                            />
                            {touched.endDate && errors.endDate && (
                              <WarningText>
                                {errors.endDate}
                              </WarningText>
                            )}
                          </FormField>
                        )}
                      </Row>
                      {values.professionals.length > 0 && (
                        <Row>
                          <FormField>
                            <Select
                              name='startTime'
                              placeholder='Horário inicial...'
                              value={values.startTime}
                              options={startTimeWindows}
                              onChange={handleTimeSelected}
                              onBlur={setFieldTouched}
                              error={errors.startTime}
                              touched={touched.startTime}
                            />
                            {touched.startTime && errors.startTime && (
                              <WarningText>
                                {errors.startTime}
                              </WarningText>
                            )}
                          </FormField>
                          <FormField>
                            <Select
                              name='endTime'
                              placeholder='Horário final...'
                              value={values.endTime}
                              options={endTimeWindows}
                              onChange={handleTimeSelected}
                              onBlur={setFieldTouched}
                              error={errors.endTime}
                              touched={touched.endTime}
                            />
                            {touched.endTime && errors.endTime && (
                              <WarningText>
                                {errors.endTime}
                              </WarningText>
                            )}
                          </FormField>
                        </Row>
                      )}
                      {errorMessage && (
                        <ErrorContainer>
                          <WhiteText>{errorMessage}</WhiteText>
                        </ErrorContainer>
                      )}
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Criar data excepcional'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={180}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
